export const AUTH_POPUP_EVENT = "auth-popup-finalized";
export const AUTH_POPUP_LEGACY_EVENT = "finalized";
export const AUTH_POPUP_STORAGE_PREFIX = "auth-popup:";
export const AUTH_POPUP_PENDING_KEY = `${AUTH_POPUP_STORAGE_PREFIX}pending`;

export interface AuthPopupMessage {
    channel: string;
    href?: string;
    search?: string;
    type: typeof AUTH_POPUP_EVENT;
}

export function createAuthPopupChannel(prefix: string) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getAuthPopupStorageKey(channel: string) {
    return `${AUTH_POPUP_STORAGE_PREFIX}${channel}`;
}

export function setPendingAuthPopupChannel(channel: string) {
    try {
        localStorage.setItem(
            AUTH_POPUP_PENDING_KEY,
            JSON.stringify({
                channel,
                timestamp: Date.now(),
            }),
        );
    } catch {
        return;
    }
}

export function consumePendingAuthPopupChannel(maxAgeMs = 10 * 60 * 1000) {
    try {
        const raw = localStorage.getItem(AUTH_POPUP_PENDING_KEY);

        if (!raw) {
            return undefined;
        }

        const payload = JSON.parse(raw) as {
            channel?: string;
            timestamp?: number;
        };

        localStorage.removeItem(AUTH_POPUP_PENDING_KEY);

        if (
            typeof payload.channel === "string" &&
            typeof payload.timestamp === "number" &&
            Date.now() - payload.timestamp <= maxAgeMs
        ) {
            return payload.channel;
        }
    } catch {
        return undefined;
    }

    return undefined;
}

export function createAuthPopupMessage(channel: string): AuthPopupMessage {
    return {
        channel,
        href: window.location.href,
        search: window.location.search,
        type: AUTH_POPUP_EVENT,
    };
}

export function notifyAuthPopupComplete(channel: string) {
    const message = createAuthPopupMessage(channel);

    if (window.opener) {
        try {
            window.opener.dispatchEvent(new Event(AUTH_POPUP_LEGACY_EVENT));
        } catch {
            // Ignore cross-window event dispatch failures and continue with fallbacks.
        }

        try {
            window.opener.dispatchEvent(
                new CustomEvent(AUTH_POPUP_EVENT, {
                    detail: message,
                }),
            );
        } catch {
            // Ignore cross-window event dispatch failures and continue with fallbacks.
        }

        try {
            window.opener.postMessage(message, window.location.origin);
        } catch {
            // Ignore and continue with same-origin fallbacks below.
        }
    }

    if ("BroadcastChannel" in window) {
        try {
            const channelRef = new BroadcastChannel(channel);
            channelRef.postMessage(message);
            channelRef.close();
        } catch {
            // Ignore and continue with storage fallback.
        }
    }

    try {
        const storageKey = getAuthPopupStorageKey(channel);
        localStorage.setItem(
            storageKey,
            JSON.stringify({
                ...message,
                timestamp: Date.now(),
            }),
        );
    } catch {
        // Ignore storage failures. The popup still attempts to close itself.
    }

    return message;
}
